import type Phaser from "phaser";
import { CANVAS_COLOURS, cssColour, forEachPatternMark, type ActivationCategoryStyle, type CanvasColour, type PresenceArea } from "daedalus-core";
import { forEachBoundaryRun, forEachRun, meets, type CellWindow } from "./cell-runs.ts";

/**
 * Activation on the map (P1.6-T32, ADR-070's amendments of 2026-09-26 and 2026-09-27). Graphics over the props:
 * the Activation overlay, every behaviour prop's cells in its type's category (a 30% fill, the category's
 * pattern, a 2 px edge in the colour along the area's boundary and a 1 px dark keyline outside it, a default
 * area ghosted), an outline outside the area while the prop has an issue and a tag with the type's number at
 * the area's top-left; above it the selected behaviour prop's footprint, cells and feet point, drawn as the
 * cell editors draw them. The edge, the keyline, the pattern and the tags keep their screen size at any zoom.
 * Both are redrawn only when what they draw changes or the zoom does; the overlay draws only the cells inside a
 * window around the camera's view, half a view wider on every side, and is drawn again only when the view
 * leaves that window. `sync`, called every frame, compares numbers and allocates nothing.
 */

/**
 * One behaviour prop of the overlay: its cells, whether they are its default cells, its type's number on the
 * map and the category style that number draws in, and its worst issue.
 */
export interface ActivationCells {
  readonly cells: PresenceArea;
  readonly defaulted: boolean;
  readonly number: number;
  readonly style: ActivationCategoryStyle;
  readonly issue: "error" | "warning" | null;
}

/**
 * The selected behaviour prop as the map draws it: its cells, whether they are its default cells, the cells
 * its footprint blocks and its feet point in world pixels.
 */
export interface ActivationSelection {
  readonly cells: PresenceArea;
  readonly defaulted: boolean;
  readonly footprint: PresenceArea;
  readonly feet: { readonly x: number; readonly y: number };
}

/**
 * The opacity of painted cells, as the activation editors fill them, for the selected prop.
 */
export const ACTIVATION_FILL = 0.35;

/**
 * The opacity of an overlay area's fill, the design card's 30%.
 */
export const CATEGORY_FILL = 0.3;

/**
 * Screen pixels a pattern's design unit: the card's map, drawn at zoom 2, so the 6-unit tile is 12 px on screen.
 */
export const PATTERN_UNIT_PX = 2;

/**
 * The width of an overlay area's edge in its colour, in screen pixels.
 */
export const CATEGORY_EDGE_PX = 2;

/**
 * The width of the dark keyline outside an overlay area's edge, in screen pixels.
 */
export const CATEGORY_KEYLINE_PX = 1;

/**
 * The number tag's height and least width, in screen pixels (the card's `d-catnum`).
 */
export const TAG_SIZE_PX = 18;

/**
 * The tag's inset from the area's top-left, in screen pixels.
 */
export const TAG_INSET_PX = 4;

/**
 * The least side of an area's box on screen, in pixels, that carries a tag: the tag and its inset on both sides.
 */
export const TAG_MIN_AREA_PX = TAG_SIZE_PX + 2 * TAG_INSET_PX;

/**
 * The tag's face, the editor's mono face, with its fallbacks.
 */
export const TAG_FONT_FAMILY = '"JetBrains Mono", ui-monospace, Consolas, monospace';

/**
 * The tag's size and weight: 11 px at 500, the card's `d-catnum`.
 */
export const TAG_FONT_SIZE = "11px";
const TAG_FONT_WEIGHT = "500";

/**
 * The opacity of default cells, as the cell editors ghost them.
 */
export const ACTIVATION_GHOST = 0.14;

/**
 * The opacity of the footprint's cells, as the footprint editors fill them.
 */
export const FOOTPRINT_FILL = 0.4;

/**
 * The smallest cell on screen, in pixels, that carries its 1 px edge, as in the cell editors.
 */
export const EDGE_MIN_CELL = 5;

/**
 * The opacity of a default cell's edge, so a ghosted ring reads over any ground on the map.
 */
export const GHOST_EDGE = 0.5;

const FEET_ARM = 5;
const FEET_LINE = 2;
const ISSUE_LINE = 1;
const ISSUE_GAP_PX = 2;
const TAG_PADDING_PX = 4;
const EVERYWHERE: CellWindow = { x0: Number.NEGATIVE_INFINITY, y0: Number.NEGATIVE_INFINITY, x1: Number.POSITIVE_INFINITY, y1: Number.POSITIVE_INFINITY };

/**
 * The overlay and the selection of one scene.
 */
export class ActivationView {
  private readonly overlay: Phaser.GameObjects.Graphics;
  private readonly selection: Phaser.GameObjects.Graphics;
  private readonly tagPlates: Phaser.GameObjects.Graphics;
  private readonly tags: Phaser.GameObjects.Text[] = [];
  private tagsShown = 0;
  private entries: readonly ActivationCells[] | null = null;
  private selected: ActivationSelection | null = null;
  private overlayDirty = false;
  private selectionDirty = false;
  private overlayZoom = 0;
  private selectionZoom = 0;
  private windowX0 = 0;
  private windowY0 = 0;
  private windowX1 = -1;
  private windowY1 = -1;
  private overlayDraws = 0;
  private selectionDraws = 0;

  /**
   * @param scene Scene to draw into.
   * @param cellPixels Pixels per collision cell.
   * @param depth The overlay's depth, over every prop; the selection draws just above it and the number tags
   *   above both.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly cellPixels: number,
    private readonly depth: number,
  ) {
    this.overlay = scene.add.graphics().setDepth(depth);
    this.selection = scene.add.graphics().setDepth(depth + 1);
    this.tagPlates = scene.add.graphics().setDepth(depth + 2);
    this.awaitTagFont();
  }

  /**
   * Draws the tags again once the mono face has loaded: a canvas draws text in whatever face is loaded when the
   * text is drawn, and the page loads a web font only when something asks for it.
   */
  private awaitTagFont(): void {
    const fonts = typeof document === "undefined" ? undefined : document.fonts;
    if (fonts === undefined) {
      return;
    }
    fonts.load(`${TAG_FONT_WEIGHT} ${TAG_FONT_SIZE} ${TAG_FONT_FAMILY}`).then(
      () => {
        for (const tag of this.tags) {
          tag.updateText();
        }
        this.overlayDirty = true;
      },
      () => undefined,
    );
  }

  /**
   * The number tags drawn at the last overlay draw, for the page's report.
   *
   * @returns The count.
   */
  public get tagCount(): number {
    return this.tagsShown;
  }

  /**
   * How many times the overlay has been drawn.
   *
   * @returns The count.
   */
  public get overlayRedraws(): number {
    return this.overlayDraws;
  }

  /**
   * How many times the selection has been drawn.
   *
   * @returns The count.
   */
  public get selectionRedraws(): number {
    return this.selectionDraws;
  }

  /**
   * Sets the overlay's props, or hides it; it is drawn at the next `sync`.
   *
   * @param entries The props, or null while the overlay is off.
   */
  public setOverlay(entries: readonly ActivationCells[] | null): void {
    this.entries = entries;
    this.overlayDirty = true;
  }

  /**
   * Sets the selected behaviour prop, or none; it is drawn at the next `sync`.
   *
   * @param selected The prop, or null.
   */
  public setSelection(selected: ActivationSelection | null): void {
    this.selected = selected;
    this.selectionDirty = true;
  }

  /**
   * Draws what changed for the camera's view: the overlay when its props or the zoom changed or the view left
   * the window last drawn, the selection when it or the zoom changed. Takes the view as numbers, so a caller
   * each frame allocates nothing.
   *
   * @param viewLeft The view's left edge in world pixels.
   * @param viewTop The view's top edge in world pixels.
   * @param viewWidth The view's width in world pixels.
   * @param viewHeight The view's height in world pixels.
   * @param zoom The camera's zoom.
   */
  public sync(viewLeft: number, viewTop: number, viewWidth: number, viewHeight: number, zoom: number): void {
    const cell = this.cellPixels;
    const left = Math.floor(viewLeft / cell);
    const top = Math.floor(viewTop / cell);
    const right = Math.floor((viewLeft + viewWidth) / cell);
    const bottom = Math.floor((viewTop + viewHeight) / cell);
    const outside = left < this.windowX0 || top < this.windowY0 || right > this.windowX1 || bottom > this.windowY1;
    if (this.overlayDirty || zoom !== this.overlayZoom || (this.entries !== null && outside)) {
      const spanX = right - left + 1;
      const spanY = bottom - top + 1;
      this.windowX0 = left - (spanX >> 1);
      this.windowY0 = top - (spanY >> 1);
      this.windowX1 = right + (spanX >> 1);
      this.windowY1 = bottom + (spanY >> 1);
      this.overlayZoom = zoom;
      this.overlayDirty = false;
      this.drawOverlay(zoom);
    }
    if (this.selectionDirty || zoom !== this.selectionZoom) {
      this.selectionZoom = zoom;
      this.selectionDirty = false;
      this.drawSelection(zoom);
    }
  }

  /**
   * Removes both graphics from the scene.
   */
  public destroy(): void {
    this.overlay.destroy();
    this.selection.destroy();
    this.tagPlates.destroy();
    for (const tag of this.tags) {
      tag.destroy();
    }
    this.tags.length = 0;
  }

  /**
   * Fills an area's cells in a window, with each cell's 1 px edge when a cell is large enough on screen.
   *
   * @param g The graphics.
   * @param area The area.
   * @param window The window.
   * @param colour The colour.
   * @param opacity The fill's opacity.
   * @param edge The opacity of each cell's edge, 0 for none.
   * @param zoom The camera's zoom.
   */
  private fillCells(g: Phaser.GameObjects.Graphics, area: PresenceArea, window: CellWindow, colour: CanvasColour, opacity: number, edge: number, zoom: number): void {
    const cell = this.cellPixels;
    g.fillStyle(colour.rgb, colour.alpha * opacity);
    forEachRun(area, window, (x, y, length) => g.fillRect(x * cell, y * cell, length * cell, cell));
    if (edge === 0 || cell * zoom < EDGE_MIN_CELL) {
      return;
    }
    const inset = 0.5 / zoom;
    g.lineStyle(1 / zoom, colour.rgb, colour.alpha * edge);
    forEachRun(area, window, (x, y, length) => {
      for (let i = 0; i < length; i += 1) {
        g.strokeRect((x + i) * cell + inset, y * cell + inset, cell - 2 * inset, cell - 2 * inset);
      }
    });
  }

  /**
   * Draws the overlay's props inside the window, each as its category draws it, then their number tags.
   *
   * @param zoom The camera's zoom.
   */
  private drawOverlay(zoom: number): void {
    const g = this.overlay;
    g.clear();
    this.tagPlates.clear();
    this.overlayDraws += 1;
    const entries = this.entries;
    let tags = 0;
    if (entries !== null) {
      const window: CellWindow = { x0: this.windowX0, y0: this.windowY0, x1: this.windowX1, y1: this.windowY1 };
      for (const entry of entries) {
        if (!meets(entry.cells, window)) {
          continue;
        }
        this.drawArea(g, entry, window, zoom);
        if (this.cellPixels * zoom * Math.min(entry.cells.width, entry.cells.height) >= TAG_MIN_AREA_PX) {
          this.drawTag(tags, entry, zoom);
          tags += 1;
        }
      }
    }
    for (let i = tags; i < this.tagsShown; i += 1) {
      this.tags[i]?.setVisible(false);
    }
    this.tagsShown = tags;
  }

  /**
   * Draws one area: its fill, its pattern over it, the dark keyline outside its boundary, the edge in its colour
   * inside the boundary and, for a prop with an issue, the issue's outline outside its box. A default area draws
   * its fill at the ghost's opacity and its pattern and edge at half their alpha.
   *
   * @param g The graphics.
   * @param entry The prop's area and category.
   * @param window The window of world cells.
   * @param zoom The camera's zoom.
   */
  private drawArea(g: Phaser.GameObjects.Graphics, entry: ActivationCells, window: CellWindow, zoom: number): void {
    const cell = this.cellPixels;
    const { colour, pattern } = entry.style;
    const strength = entry.defaulted ? GHOST_EDGE : 1;
    g.fillStyle(colour.rgb, colour.alpha * (entry.defaulted ? ACTIVATION_GHOST : CATEGORY_FILL));
    forEachRun(entry.cells, window, (x, y, length) => g.fillRect(x * cell, y * cell, length * cell, cell));
    const unit = PATTERN_UNIT_PX / zoom;
    let alphaSet = -1;
    /**
     * Sets the fill to the category's colour at a pattern mark's opacity, scaled for a default area, only when
     * that opacity differs from the last mark's, so a pattern's run of marks sets its style once.
     *
     * @param alpha The mark's opacity, a share of the colour's own.
     */
    const setAlpha = (alpha: number): void => {
      if (alpha !== alphaSet) {
        alphaSet = alpha;
        g.fillStyle(colour.rgb, colour.alpha * alpha * strength);
      }
    };
    forEachRun(entry.cells, window, (x, y, length) =>
      forEachPatternMark(
        pattern,
        x * cell,
        y * cell,
        length * cell,
        cell,
        unit,
        (mx, my, mw, mh, alpha) => {
          setAlpha(alpha);
          g.fillRect(mx, my, mw, mh);
        },
        (points, count, alpha) => {
          setAlpha(alpha);
          g.beginPath();
          g.moveTo(points[0] as number, points[1] as number);
          for (let i = 1; i < count; i += 1) {
            g.lineTo(points[2 * i] as number, points[2 * i + 1] as number);
          }
          g.closePath();
          g.fillPath();
        },
      ),
    );
    const keyline = CANVAS_COLOURS.activationKeyline;
    this.stripBoundary(g, entry.cells, window, keyline, keyline.alpha, CATEGORY_KEYLINE_PX / zoom, false);
    this.stripBoundary(g, entry.cells, window, colour, colour.alpha * strength, CATEGORY_EDGE_PX / zoom, true);
    if (entry.issue !== null) {
      const mark = entry.issue === "error" ? CANVAS_COLOURS.activationIssueError : CANVAS_COLOURS.activationIssueWarning;
      const gap = (ISSUE_GAP_PX + ISSUE_LINE / 2) / zoom;
      g.lineStyle(ISSUE_LINE / zoom, mark.rgb, mark.alpha);
      g.strokeRect(entry.cells.x0 * cell - gap, entry.cells.y0 * cell - gap, entry.cells.width * cell + 2 * gap, entry.cells.height * cell + 2 * gap);
    }
  }

  /**
   * Fills a strip along each boundary run of an area, inside the boundary or outside it. A strip outside runs on
   * past both ends of its run by its width, so the strips meet at a convex corner; the edge drawn inside after it
   * covers what that runs into at a concave one.
   *
   * @param g The graphics.
   * @param area The area.
   * @param window The window of world cells.
   * @param colour The strip's colour.
   * @param alpha The strip's opacity.
   * @param width The strip's width in world pixels.
   * @param inside Whether the strip lies inside the boundary.
   */
  private stripBoundary(g: Phaser.GameObjects.Graphics, area: PresenceArea, window: CellWindow, colour: CanvasColour, alpha: number, width: number, inside: boolean): void {
    const cell = this.cellPixels;
    const run = inside ? 0 : width;
    g.fillStyle(colour.rgb, alpha);
    forEachBoundaryRun(area, window, (side, x, y, length) => {
      const left = x * cell;
      const top = y * cell;
      if (side === "top" || side === "bottom") {
        const edge = side === "top" ? top : top + cell;
        const across = (side === "top") === inside ? edge : edge - width;
        g.fillRect(left - run, across, length * cell + 2 * run, width);
      } else {
        const edge = side === "left" ? left : left + cell;
        const across = (side === "left") === inside ? edge : edge - width;
        g.fillRect(across, top - run, width, length * cell + 2 * run);
      }
    });
  }

  /**
   * Draws one number tag at an area's top-left: a plate in the category's colour inside a dark ring, and the
   * number over it, at a fixed screen size.
   *
   * @param index The tag's place in the pool.
   * @param entry The prop's area and category.
   * @param zoom The camera's zoom.
   */
  private drawTag(index: number, entry: ActivationCells, zoom: number): void {
    let text = this.tags[index];
    if (text === undefined) {
      text = this.scene.add.text(0, 0, "", { fontFamily: TAG_FONT_FAMILY, fontSize: TAG_FONT_SIZE, fontStyle: TAG_FONT_WEIGHT, color: cssColour(CANVAS_COLOURS.activationKeyline) }).setOrigin(0.5, 0.5).setDepth(this.depth + 3);
      this.tags.push(text);
    }
    const label = String(entry.number);
    if (text.text !== label) {
      text.setText(label);
    }
    const colour = entry.style.colour;
    const ring = CANVAS_COLOURS.activationKeyline;
    const px = 1 / zoom;
    const width = Math.max(TAG_SIZE_PX, text.width + 2 * TAG_PADDING_PX) * px;
    const height = TAG_SIZE_PX * px;
    const left = entry.cells.x0 * this.cellPixels + TAG_INSET_PX * px;
    const top = entry.cells.y0 * this.cellPixels + TAG_INSET_PX * px;
    const plates = this.tagPlates;
    plates.fillStyle(ring.rgb, ring.alpha);
    plates.fillRect(left - CATEGORY_KEYLINE_PX * px, top - CATEGORY_KEYLINE_PX * px, width + 2 * CATEGORY_KEYLINE_PX * px, height + 2 * CATEGORY_KEYLINE_PX * px);
    plates.fillStyle(colour.rgb, colour.alpha);
    plates.fillRect(left, top, width, height);
    text.setScale(px).setPosition(left + width / 2, top + height / 2).setVisible(true);
  }

  /**
   * Draws the selected prop: its footprint, then its cells, then its feet point.
   *
   * @param zoom The camera's zoom.
   */
  private drawSelection(zoom: number): void {
    const g = this.selection;
    g.clear();
    this.selectionDraws += 1;
    const selected = this.selected;
    if (selected === null) {
      return;
    }
    this.fillCells(g, selected.footprint, EVERYWHERE, CANVAS_COLOURS.footprintCell, FOOTPRINT_FILL, 0, zoom);
    if (selected.defaulted) {
      this.fillCells(g, selected.cells, EVERYWHERE, CANVAS_COLOURS.cellGhost, ACTIVATION_GHOST, GHOST_EDGE, zoom);
    } else {
      this.fillCells(g, selected.cells, EVERYWHERE, CANVAS_COLOURS.activationCell, ACTIVATION_FILL, 1, zoom);
    }
    const feet = CANVAS_COLOURS.activationFeet;
    const arm = FEET_ARM / zoom;
    g.lineStyle(FEET_LINE / zoom, feet.rgb, feet.alpha);
    g.lineBetween(selected.feet.x - arm, selected.feet.y, selected.feet.x + arm, selected.feet.y);
    g.lineBetween(selected.feet.x, selected.feet.y - arm, selected.feet.x, selected.feet.y + arm);
  }
}
